/**
 * Storing reminder schedules (T-701).
 *
 * They live in the **settings store** as one JSON value, not in a collection of
 * their own. Three reasons, and the first is the one that matters:
 *
 *   - The backup already carries the whole settings store verbatim (T-502),
 *     precisely so that a key added later is in every backup taken after it is
 *     added rather than silently absent from every one taken before someone
 *     remembered to update the export. Schedules were the worked example in
 *     that ticket's comment; this is it arriving.
 *   - No schema migration, so a build that predates reminders opens a database
 *     containing them without complaint.
 *   - There are single digits of these. A table, an index and a conformance
 *     case each would be cost with nothing on the other side.
 *
 * The repository is the same shape as the others, so no screen knows any of the
 * above.
 */

import {
  nextOccurrence,
  validateReminder,
  type Recurrence,
  type ReminderSchedule,
} from '../model/reminder.js'
import { err, invalid, ok, type Result } from '../model/result.js'
import type { Timestamp } from '../model/timestamp.js'
import type { Clock } from '../ports/clock.js'
import type { IdGenerator } from '../ports/id-generator.js'
import type { Storage } from '../ports/storage.js'

export const REMINDERS_KEY = 'anytracker.reminders'

export interface NewReminder {
  readonly name: string
  readonly recurrence: Recurrence
  readonly templateId?: string
  readonly enabled?: boolean
}

export type ReminderChanges = {
  readonly [K in keyof Omit<ReminderSchedule, 'id' | 'createdAt'>]?:
    ReminderSchedule[K] | undefined
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readTimestamp(value: unknown): Timestamp | null {
  if (!isRecord(value)) {
    return null
  }
  return typeof value['epochMs'] === 'number' && typeof value['timeZone'] === 'string'
    ? { epochMs: value['epochMs'], timeZone: value['timeZone'] }
    : null
}

/**
 * Reads the stored list, discarding anything that is not a schedule.
 *
 * Settings are strings, and a string can be anything — a truncated write, a key
 * collision, a hand-edited backup. Dropping the unreadable entries rather than
 * throwing keeps one bad record from taking the whole screen down with it, and
 * the ones that are fine still work.
 */
function parse(raw: string | undefined): ReminderSchedule[] {
  if (raw === undefined) {
    return []
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) {
    return []
  }

  const schedules: ReminderSchedule[] = []
  for (const candidate of parsed) {
    if (!isRecord(candidate)) {
      continue
    }
    const createdAt = readTimestamp(candidate['createdAt'])
    const updatedAt = readTimestamp(candidate['updatedAt'])
    const recurrence = candidate['recurrence']
    if (
      typeof candidate['id'] !== 'string' ||
      typeof candidate['name'] !== 'string' ||
      typeof candidate['enabled'] !== 'boolean' ||
      createdAt === null ||
      updatedAt === null ||
      !isRecord(recurrence)
    ) {
      continue
    }
    const templateId = candidate['templateId']
    schedules.push({
      id: candidate['id'],
      name: candidate['name'],
      enabled: candidate['enabled'],
      recurrence: recurrence as unknown as Recurrence,
      createdAt,
      updatedAt,
      ...(typeof templateId === 'string' ? { templateId } : {}),
    })
  }
  return schedules
}

export interface ReminderDeps {
  readonly storage: Storage
  readonly clock: Clock
  readonly ids: IdGenerator
}

export class ReminderRepository {
  constructor(private readonly deps: ReminderDeps) {}

  async list(): Promise<ReminderSchedule[]> {
    const raw = await this.deps.storage.settings.get(REMINDERS_KEY)
    return parse(raw).sort(
      (a, b) => a.createdAt.epochMs - b.createdAt.epochMs || a.id.localeCompare(b.id),
    )
  }

  async get(id: string): Promise<ReminderSchedule | undefined> {
    return (await this.list()).find((schedule) => schedule.id === id)
  }

  private async write(schedules: readonly ReminderSchedule[]): Promise<void> {
    await this.deps.storage.settings.set(REMINDERS_KEY, JSON.stringify(schedules))
  }

  async create(input: NewReminder): Promise<Result<ReminderSchedule>> {
    const now = this.deps.clock.now()
    const existing = await this.list()
    const schedule: ReminderSchedule = {
      id: this.deps.ids.next(),
      name: input.name,
      enabled: input.enabled ?? true,
      recurrence: input.recurrence,
      ...(input.templateId === undefined ? {} : { templateId: input.templateId }),
      createdAt: now,
      updatedAt: now,
    }

    const validated = validateReminder(schedule, { existing })
    if (!validated.ok) {
      return validated
    }
    await this.write([...existing, schedule])
    return ok(schedule)
  }

  async update(id: string, changes: ReminderChanges): Promise<Result<ReminderSchedule>> {
    const existing = await this.list()
    const current = existing.find((schedule) => schedule.id === id)
    if (current === undefined) {
      return err<ReminderSchedule>(
        invalid('reminder.notFound', 'That reminder no longer exists.'),
      )
    }

    // An explicit `undefined` clears the key rather than storing it, so a
    // reminder that had a check-in and no longer does carries no
    // present-but-empty field into the backup.
    const merged: Record<string, unknown> = { ...current }
    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined) {
        delete merged[key]
      } else {
        merged[key] = value
      }
    }

    const updated = {
      ...merged,
      id: current.id,
      createdAt: current.createdAt,
      updatedAt: this.deps.clock.now(),
    } as ReminderSchedule

    const validated = validateReminder(updated, { existing })
    if (!validated.ok) {
      return validated
    }
    await this.write(
      existing.map((schedule) => (schedule.id === id ? updated : schedule)),
    )
    return ok(updated)
  }

  setEnabled(id: string, enabled: boolean): Promise<Result<ReminderSchedule>> {
    return this.update(id, { enabled })
  }

  /**
   * Deleted outright, not archived.
   *
   * Unlike a track, a reminder holds no history: nothing was ever recorded
   * against it, so there is nothing archiving would protect.
   */
  async remove(id: string): Promise<Result<ReminderSchedule>> {
    const existing = await this.list()
    const found = existing.find((schedule) => schedule.id === id)
    if (found === undefined) {
      return err<ReminderSchedule>(
        invalid('reminder.notFound', 'That reminder no longer exists.'),
      )
    }
    await this.write(existing.filter((schedule) => schedule.id !== id))
    return ok(found)
  }

  /** When each enabled schedule next fires, for a list that shows it. */
  async nextOccurrences(): Promise<Map<string, Timestamp>> {
    const now = this.deps.clock.now()
    const timeZone = this.deps.clock.timeZone()
    const next = new Map<string, Timestamp>()
    for (const schedule of await this.list()) {
      const at = nextOccurrence(schedule, now, timeZone)
      if (at !== null) {
        next.set(schedule.id, at)
      }
    }
    return next
  }
}
